<template>
  <div class="column-picker">
    <div class="column-picker__list">
      <FormGroup :label="labelOut || 'Hidden columns'">
        <DsInput style="margin-bottom: 1rem;" placeholder="Search field..." is-simplified v-model="queryHidden" @update:modelValue="filterHiddenColumns"/>
        <draggable class="draggable-list" :list="hiddenColumns" @change="apply" item-key="id" group="columns">
          <template #item="{ element }">
            <div class="draggable-list-item" @click="toggleColumn(element, 'visible')">
              {{ element.humanizedName }}
            </div>
          </template>
        </draggable>
      </FormGroup>
    </div>

    <div class="column-picker__center-container">
      <Icon name="expand"/>
    </div>

    <div class="column-picker__list">
      <form-group :label="labelIn || 'Visible columns'">
        <DsInput style="margin-bottom: 1rem;" placeholder="Search field..." is-simplified v-model="queryVisible" @update:modelValue="filterVisibleColumns"/>
        <draggable class="draggable-list" :list="visibleColumns" @change="apply" item-key="id" group="columns">
          <template #item="{ element }">
            <div class="draggable-list-item" @click="toggleColumn(element, 'hidden')">
              {{ element.humanizedName }}
            </div>
          </template>
        </draggable>
      </form-group>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import draggable from 'vuedraggable';
  import _difference from 'lodash/difference.js';
  import { humanize } from '../../constants/properties.js';
  import FormGroup from '@/components/Form/FormGroup.vue'
  import Icon from '@/components/Icon/Icon.vue'
  import { _unique, getReportFieldById } from '@/util/helpers'
  import { viewListOptions } from '@/constants/config'

  export default defineComponent({
    name: 'DraggableColumnPicker',
    components: { Icon, FormGroup, draggable },
    props: {
      labelIn: String,
      labelOut: String,
      options: Array,
      modelValue: Array,
    },
    emits: ['update:modelValue'],
    data() {
      return {
        hiddenColumns: [],
        visibleColumns: [],
        hiddenColumnsCopy: [],
        visibleColumnsCopy: [],
        queryHidden: '',
        queryVisible: '',
      };
    },
    computed: {
      leftovers() {
        var leftovers = _difference(this.options || viewListOptions, this.modelValue).sort()

        if (!this.isInFundingDashboard) {
          var fundingDashboardColumns = ['total_investment', 'funding_date', 'funding_type', 'funding_amount']

          leftovers = leftovers.filter(column => {
            return !fundingDashboardColumns.includes(column)
          })
        }

        leftovers = leftovers.map(column => {
          return { id: column, name: column, humanizedName: this.humanizeColumn(column) }
        })

        return _unique(leftovers)
      },
      isInFundingDashboard() {
        return this.$route.path === '/dashboards/funding-list'
      },
    },
    methods: {
      humanizeColumn(column) {
        if (column.indexOf('report_field_') >= 0) {
          var reportFieldId = column.replace('report_field_', '')
          var reportField = getReportFieldById(reportFieldId)

          if (!reportField) {
            return 'report'
          }

          return reportField.label
        }

        return humanize(column)
      },
      filterHiddenColumns(value) {
        if (!value) {
          this.hiddenColumns = this.hiddenColumnsCopy;
        }

        this.hiddenColumns = this.hiddenColumns.filter(item => item.humanizedName.toLowerCase().includes(value.toLowerCase()));
      },
      filterVisibleColumns(value) {
        if (!value) {
          this.visibleColumns = this.visibleColumnsCopy;
        }

        this.visibleColumns = this.visibleColumns.filter(item => item.humanizedName.toLowerCase().includes(value.toLowerCase()));
      },
      apply() {
        this.$emit('update:modelValue', this.visibleColumns.map(item => item.id));
      },
      toggleColumn(column, targetList) {
        if (targetList === 'visible') {
          // Verwijder het veld uit de kopie (back-up) van hiddenColumns
          this.hiddenColumnsCopy = this.hiddenColumnsCopy.filter(item => item.id !== column.id);
          this.resetHiddenColumns();
          // Voeg toe aan visibleColumns
          this.visibleColumns.push(column);
          this.visibleColumnsCopy = [...this.visibleColumns]; // sync de kopie

          this.queryHidden = ''; // reset de zoekbalk
        } else {
          this.visibleColumnsCopy = this.visibleColumnsCopy.filter(item => item.id !== column.id);
          this.resetVisibleColumns();
          this.hiddenColumns.push(column);
          this.hiddenColumnsCopy = [...this.hiddenColumns]; // sync de kopie

          this.queryVisible = '';
        }
        setTimeout(() => {
          this.apply();
        }, 200);
      },
      resetHiddenColumns() {
        this.hiddenColumns = [...this.hiddenColumnsCopy];
      },
      resetVisibleColumns() {
        this.visibleColumns = [...this.visibleColumnsCopy];
      },
      mapColumn(column) {
        if (column === 'elastic_id') return { id: 'elastic_id', humanizedName: 'ID' };
        if (column === 'stage') return { id: 'stage', humanizedName: 'Growth Stage' };
        if (column == 'industry_codes') return { id: column, name: column, humanizedName: 'Main NACE Code' }
        if (column == 'nace_codes') return { id: column, name: column, humanizedName: 'Main NACE Code' }
        if (column == 'claimees') return { id: column, name: column, humanizedName: 'Claimees (Contributors of the actor)' }
        if (column == 'contact_email_list') return { id: column, name: column, humanizedName: 'Founder & Employees (can be non-contributors)' }

        return { id: column, humanizedName: humanize(column) };
      },
      sortByHumanizedName(a, b) {
        return a.humanizedName.localeCompare(b.humanizedName);
      },
    },
    created() {
      this.hiddenColumns = this.leftovers;
      this.hiddenColumnsCopy = this.hiddenColumns;
      this.visibleColumns = (this.modelValue || []).map(this.mapColumn);
      this.visibleColumnsCopy = this.visibleColumns;
    },
  });
</script>

<style lang="scss" scoped>
  .column-picker {
    display: flex;
    gap: 20px;
    align-items: start;
    max-height: 340px;
  }

  .column-picker__list {
    flex: 1;
  }

  .draggable-list {
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 10px;
    min-height: 200px;
    background: #f9f9f9;
    max-height: 300px;
    overflow: scroll;
  }

  .draggable-list-item {
    padding: 8px 12px;
    margin: 4px 0;
    background: white;
    border-radius: 5px;
    cursor: pointer;
    transition: background 0.3s;
    border: 1px solid #e3e3e3;
  }

  .draggable-list-item:hover {
    background: #e3e3e3;
    border: 1px solid var(--primary);
  }

  .column-picker__center-container {
    align-content: center;
    height: 300px;

    :deep(path) {
      fill: var(--primary) !important;
    }

  }
</style>
